'use client';
import React, { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const whyReasons = [
  { icon: 'TagIcon', title: 'Keep Your Brand', desc: 'Build customer loyalty under your own identity. Full white-label ownership of your apps and platform.' },
  { icon: 'CurrencyRupeeIcon', title: 'Reduce Technology Costs', desc: 'Enterprise-grade solutions without heavy upfront investments. Pay as you grow.' },
  { icon: 'TruckIcon', title: 'Increase Fleet Utilization', desc: 'Reduce idle time and maximize operational efficiency with AI-powered dispatch and routing.' },
  { icon: 'UserGroupIcon', title: 'Lower Customer Acquisition Cost', desc: 'Leverage local trust with digital convenience. Your community already knows you.' },
  { icon: 'RocketLaunchIcon', title: 'Expand Faster', desc: 'Launch new cities with a proven, scalable operating model. Replicate success across markets.' },
  { icon: 'BoltIcon', title: 'Compete with Giants', desc: 'Use technology, local relationships, and operational expertise to win your market.' },
];

const successStats = [
  { value: '22+', label: 'Cities. One Growing Network.', desc: 'From city-based taxi operators to regional transport associations, powering mobility businesses across Tamil Nadu and expanding into new states.' },
  { value: '5,500+', label: 'Trips Every Day', desc: 'Our platform processes thousands of real customer trips every day, proving scalability, reliability, and operational strength in live markets.' },
  { value: '6', label: 'Partner Companies', desc: 'Trusted by established local operators who have built strong customer relationships and are now accelerating growth through digital transformation.' },
];

export default function TestimonialsSection() {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-on-scroll').forEach((el) => {
              el.classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);

    const cards = document.querySelectorAll('.spotlight-card');
    const handleMove = (e: MouseEvent) => {
      cards.forEach((card) => {
        const el = card as HTMLElement;
        const rect = el.getBoundingClientRect();
        el.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
        el.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
      });
    };
    document.addEventListener('mousemove', handleMove);
    return () => {
      observer.disconnect();
      document.removeEventListener('mousemove', handleMove);
    };
  }, []);

  return (
    <>
      {/* Why Movio Section */}
      <section ref={ref} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-16">
            <span className="section-label block mb-3">Why Movio</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Why Operators Choose{' '}
              <span className="italic font-light text-[#0066FF]">Movio</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyReasons.map((reason, i) => (
              <div
                key={reason.title}
                className={`spotlight-card animate-on-scroll stagger-${i + 2} bg-[#FFFFFF] border border-[rgba(15,23,42,0.07)] rounded-2xl p-8 group hover:shadow-card-hover transition-all duration-300`}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300"
                  style={{ background: 'linear-gradient(135deg, rgba(0, 102, 255,0.12), rgba(7, 93, 223,0.08))', border: '1px solid rgba(0, 102, 255,0.15)' }}
                >
                  <Icon name={reason.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0066FF]" />
                </div>
                <h3 className="font-display text-xl font-semibold text-[#0F172A] mb-3">{reason.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{reason.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories / Traction */}
      <section className="py-24 bg-[#FFFFFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="animate-on-scroll stagger-1 text-center mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(0, 102, 255,0.3)] bg-[rgba(0, 102, 255,0.08)] text-[11px] font-semibold tracking-widest text-[#0066FF] uppercase mb-4">
              Success Stories
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-4">
              Real Operators.{' '}
              <span className="italic font-light text-[#0066FF]">Real Impact.</span>
            </h2>
            <p className="text-[#475569] text-lg max-w-2xl mx-auto leading-relaxed">
              Movio is already transforming how local operators compete, scale, and lead in their markets.
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {successStats.map((stat, i) => (
              <div
                key={stat.label}
                className={`spotlight-card animate-on-scroll stagger-${i + 2} bg-white border border-[rgba(0, 102, 255,0.15)] rounded-2xl p-8 flex flex-col group shadow-card`}
              >
                <div className="font-display text-5xl font-semibold text-[#0F172A] mb-2">
                  {stat.value}
                </div>
                <div className="text-[#0066FF] font-semibold text-lg mb-4">{stat.label}</div>
                <p className="text-[#475569] text-sm leading-relaxed flex-1">{stat.desc}</p>
              </div>
            ))}
          </div>

          {/* Bottom expansion note */}
          <div className="animate-on-scroll stagger-5 bg-white border border-[rgba(0, 102, 255,0.15)] rounded-2xl p-8 text-center shadow-card">
            <h3 className="font-display text-2xl font-semibold text-[#0F172A] mb-3">Expanding Across India</h3>
            <p className="text-[#475569] text-base leading-relaxed max-w-3xl mx-auto mb-6">
              With successful launches and partnerships in Tamil Nadu, Kerala, Karnataka, and Chhattisgarh, Movio is building the foundation for a nationwide operator-powered mobility network. Our partners are not just adopting technology—they are building stronger brands, increasing fleet utilization, improving customer retention, and creating new entrepreneurial opportunities in their communities.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {['Tamil Nadu', 'Kerala', 'Karnataka', 'Chhattisgarh', 'Pan India →'].map((loc) => (
                <span key={loc} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#FFFFFF] border border-[rgba(0, 102, 255,0.2)] text-sm font-medium text-[#475569]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF]" />
                  {loc}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}